import {
  Content,
  ContentForm,
  FullContent,
  SectionContent,
} from 'react-content-engine/src/data'
import { API_URL } from '../../env'
import { store } from '../../store/store'
import { logAndReject } from '../../utils/promise'
import { getClient } from '../openapi'

export const DEFAULT_PAGE = '__default__'

interface IContent {
  [key: string]: ContentForm
}

function formatContent(content: any): FullContent {
  const pageContent: IContent = {}
  const defaultContent: IContent = {}
  for (const contentObject of content) {
    if (contentObject.section !== DEFAULT_PAGE) {
      pageContent[contentObject.key] = {
        singular: contentObject.singular,
        plural: contentObject.plural,
        markdown: contentObject.markdown,
      }
    } else {
      defaultContent[contentObject.key] = {
        singular: contentObject.singular,
        plural: contentObject.plural,
        markdown: contentObject.markdown,
      }
    }
  }
  return { pageContent, defaultContent }
}

export function fetchContent(
  page: string,
  segment: string,
): Promise<FullContent> {
  const url = `${API_URL}/content/content/?page=${page}&page=__default__&segment=${segment}&format=json`

  return fetch(url)
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error('Fetch content failed')
      }
    })
    .then(data => {
      return formatContent(data)
    })
    .catch(logAndReject)
}

function formatSegments(content: any): SectionContent {
  const formattedContent: SectionContent = {
    section: null,
    content: {},
  }
  for (const contentObject of content) {
    formattedContent.section = contentObject.section
    formattedContent.content[contentObject.segment] = {
      singular: contentObject.singular,
      plural: contentObject.plural,
      markdown: contentObject.markdown,
    }
  }
  return formattedContent
}

export function fetchSegments(
  page: string,
  contentKey: string,
): Promise<SectionContent> {
  const url = `${API_URL}/content/content/?page=${page}&contentKey=${contentKey}&emptySegments=true`

  return fetch(url)
    .then(response => {
      if (response.ok) {
        return response.json()
      } else {
        throw new Error('Fetch content failed')
      }
    })
    .then(data => {
      return formatSegments(data)
    })
    .catch(logAndReject)
}

function formatSections(sections: any): string[] {
  const sectionsArr: string[] = []
  sections.forEach((section: any) => {
    Object.values(section).forEach((item: any) => {
      sectionsArr.push(item.toString())
    })
  })
  return sectionsArr
}

export function fetchSections(page: string): Promise<string[]> {
  return getClient()
    .then(client => client.apis.content.content_section_list({ page }))
    .then(response => {
      if (response.ok) {
        return formatSections(response.body)
      } else {
        throw new Error('Fetch section failed')
      }
    })
    .catch(logAndReject)
}

// tslint:disable-next-line:max-line-length
export function setContent(
  page: string,
  section: string,
  contentKey: string,
  content: Content,
): Promise<SectionContent> {
  const token = store.getState().admin.token
  if (!token) {
    throw new Error('Not logged in')
  }

  const formattedContent = {
    page,
    section,
    content_key: contentKey,
    content: Object.keys(content).map(key => ({
      segment: key,
      singular: content[key].singular,
      plural: content[key].plural,
      markdown: content[key].markdown,
    })),
  }

  const url = `${API_URL}/content/set-content/`
  const opts: {
    headers?: Array<[string, string]>
    method: string
    body: string
  } = {
    headers: [
      ['Content-type', 'application/json'],
      ['Authorization', `Token ${token}`],
    ],
    method: 'POST',
    body: JSON.stringify(formattedContent),
  }

  return fetch(url, opts).then(() => fetchSegments(page, contentKey))
}
